import React, { useEffect, useState } from "react";
import "./GroupLoanCloser.css";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { fetchUserProfile } from "../redux/slices/branch/userProfileSlice";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { ArrowLeftIcon, ArrowRightIcon, Download, FileText } from "lucide-react";

const GroupLoanCloser = () => {
  const dispatch = useDispatch();
  const { profile } = useSelector((state) => state.userProfile);
  const navigate = useNavigate();

  /* ---------------- STATES ---------------- */
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loanTypeFilter, setLoanTypeFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [groups, setGroups] = useState([]);
  const [centers, setCenters] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedCenter, setSelectedCenter] = useState("");
  const [company, setCompany] = useState(null);

  /* Modal States */
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLoanToClose, setSelectedLoanToClose] = useState(null);
  const [closingSubmitLoading, setClosingSubmitLoading] = useState(false);

  const [filters, setFilters] = useState({
    schemeName: "",
    status: "",
    loanAppliedDate: "",
  });

  const ITEMS_PER_PAGE_OPTIONS = [10, 20, 50, 100, "All"];
  const branchId = Number(localStorage.getItem("userId")) || 0;

  const formatDateDDMMYY = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = String(d.getFullYear());
    return `${dd}/${mm}/${yy}`;
  };

  useEffect(() => {
    dispatch(fetchUserProfile());
  }, [dispatch]);

  /* ---------------- FETCH CENTERS ---------------- */
  useEffect(() => {
    if (!profile?.id) return;

    const fetchCenters = async () => {
      try {
        const res = await axios.get(
          `https://payyug.in/api/group/centre?LoanofficerId=${profile.id}&limit=10000`,
        );
        setCenters(res.data?.data || []);
      } catch (error) {
        console.error("Center fetch error:", error);
      }
    };

    fetchCenters();
  }, [profile?.id]);

  /* ---------------- FETCH GROUPS ---------------- */
  useEffect(() => {
    if (!selectedCenter) {
      setGroups([]);
      return;
    }

    const fetchGroups = async () => {
      try {
        const res = await axios.get(
          `https://payyug.in/api/groupupdate/getall?LoanofficerId=${profile?.id}&CenterId=${selectedCenter}&limit=10000`,
        );
        setGroups(res.data?.data || []);
      } catch (error) {
        console.error("Group fetch error:", error);
      }
    };

    fetchGroups();
  }, [selectedCenter, profile?.id]);

  const fetchLoanReport = async (page = 1) => {
    if (!profile?.superAdminId) return;
    try {
      setLoading(true);
      const res = await axios.get(
        "https://payyug.in/api/group-loan/group/list",
        {
          params: {
            superAdminId: profile.superAdminId,
            officerId: profile.id,
            groupId: selectedGroup || undefined,
            centerId: selectedCenter || undefined,
            status:filters?.status||undefined,
            page,
            loanType: loanTypeFilter || undefined,
            limit: itemsPerPage === "All" ? 100000 : itemsPerPage,
          },
        },
      );
      setLoans(res.data.data || []);
      setCurrentPage(res.data.currentPage || 1);
      setTotalPages(res.data.totalPages || 1);
    } catch (error) {
      console.error("Error fetching loan report:", error);
      toast.error("Failed to load loan reports.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (profile?.superAdminId) {
      fetchLoanReport(currentPage);
    }
  }, [currentPage,filters?.status, itemsPerPage, profile]);


  useEffect(() => {
    const fetchCompany = async () => {
      if (!profile?.superAdminId) return;
      try {
        const res = await fetch(
          `https://payyug.in/api/get-company?superAdminId=${profile?.superAdminId}`,
        );
        const data = await res.json();
        if (res.ok && data) {
          if (data.logo)
            data.logoUrl = `https://payyug.in/${data.logo.replace(/^images\//, "")}`;
          setCompany(data);
        }
      } catch (err) {
        console.error("Failed to fetch company details", err);
      }
    };
    fetchCompany();
  }, [profile?.superAdminId]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleLoanClose = (loan) => {
    setSelectedLoanToClose(loan);
    setIsModalOpen(true);
  };

  const handleViewNocLetter = (item) => {
    navigate(`/GroupLoan/GroupLoanNOCLetter/${item.id}`, { state: item });
  };

  const handleConfirmCloseLoan = async () => {
    if (!selectedLoanToClose) return;
    try {
      setClosingSubmitLoading(true);

      const res = await axios.post(
        "https://payyug.in/api/group-loan/group/close",
        {
          grouploanAccountId: selectedLoanToClose.id,
          updatedBy: profile?.id,
        },
      );

      if (res.status === 200 || res.data?.success) {
        toast.success(
          res?.data?.message ||
            `Account ${selectedLoanToClose.loanAccountNumber} successfully closed.`,
        );
        setIsModalOpen(false);
        setSelectedLoanToClose(null);
        fetchLoanReport(currentPage);
      } else {
        toast.error(res.data?.message || "Failed to close loan account.");
      }
    } catch (error) {
      console.error("Loan close error:", error);
      toast.error(
        error.response?.data?.message ||
          "Server encountered error updating records.",
      );
    } finally {
      setClosingSubmitLoading(false);
    }
  };

  const filteredLoans = loans.filter((loan) => {
    const searchMatch =
      !searchQuery ||
      `${loan.loanAccountNumber} ${loan.loanType} ${loan.memberName} ${loan.groupLeaderName} ${loan.groupName}`
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
    const schemeMatch =
      !filters.schemeName ||
      loan.schemeName?.toLowerCase().includes(filters.schemeName.toLowerCase());
    // const statusMatch =
    //   !filters.status ||
    //   loan.status?.toLowerCase() === filters.status.toLowerCase();
    const dateMatch =
      !filters.loanAppliedDate ||
      new Date(loan.loanAppliedDate).toLocaleDateString("en-CA") ===
        filters.loanAppliedDate;
    return schemeMatch  && dateMatch && searchMatch;
  });

  const handleFilter = () => {
    setCurrentPage(1);
    fetchLoanReport(1);
  };

  /* ---------------- EXPORTS ---------------- */
  const exportExcel = () => {
    if (filteredLoans.length === 0) return;
    const data = filteredLoans.map((item) => ({
      "Account No": item.loanAccountNumber || "N/A",
      "Customer Name": item.memberName || "N/A",
      "Loan Type": `Group Loan ${item?.loanType ? `(${item?.loanType})`:""}`,
      "Loan Amount": item.loanAmount || "N/A",
      "Group Name": item.groupName || "N/A",
      "Group Leader Name": item.groupLeaderName || "N/A",
      "ROI (%)": item.loanRoi || "N/A",
      "Applied Date": item.loanAppliedDate
        ? formatDateDDMMYY(item.loanAppliedDate)
        : "N/A",
      "Disbursal Date": item.disbursedfirstDate
        ? formatDateDDMMYY(item.disbursedfirstDate)
        : "N/A",
      Status: item.status || "N/A",
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const headerCells = Object.keys(data[0]);
    headerCells.forEach((_, index) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: index });
      worksheet[cellRef].s = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "1F4E78" } },
        alignment: { horizontal: "center", vertical: "center" },
      };
    });

    worksheet["!cols"] = headerCells.map((key) => ({
      wch: Math.max(key.length + 5, 18),
    }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Loan Report");
    XLSX.writeFile(workbook, "Group_Loan_Report.xlsx");
  };

  const exportCSV = () => {
    if (filteredLoans.length === 0) return;
    const data = filteredLoans.map((item) => ({
      "Account No": item.loanAccountNumber || "N/A",
      "Customer Name": item.memberName || "N/A",
      "Loan Type": `Group Loan ${item?.loanType ? `(${item?.loanType})`:""}`,
      "Loan Amount": item.loanAmount || "N/A",
      "Group Name": item.groupName || "N/A",
      "Group Leader Name": item.groupLeaderName || "N/A",
      "ROI (%)": item.loanRoi || "N/A",
      "Applied Date": item.loanAppliedDate
        ? formatDateDDMMYY(item.loanAppliedDate)
        : "N/A",
      "Disbursal Date": item.disbursedfirstDate
        ? formatDateDDMMYY(item.disbursedfirstDate)
        : "N/A",
      Status: item.status || "N/A",
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const csvOutput = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csvOutput], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "Group_Loan_Report.csv");
  };

  const exportPDF = async () => {
    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.width;

    const logoX = 15;
    const logoY = 12;
    const logoSize = 25;
    const logoPadding = 2;

    doc.setDrawColor(0);
    doc.setLineWidth(0.2);
    doc.circle(
      logoX + logoSize / 2,
      logoY + logoSize / 2,
      logoSize / 2 + logoPadding,
    );

    if (company?.logoUrl) {
      try {
        const logoResp = await fetch(company.logoUrl);
        const logoBlob = await logoResp.blob();
        const reader = new FileReader();
        await new Promise((resolve) => {
          reader.onload = () => {
            doc.addImage(
              reader.result,
              "PNG",
              logoX,
              logoY,
              logoSize,
              logoSize,
            );
            resolve();
          };
          reader.readAsDataURL(logoBlob);
        });
      } catch {
        /* silent catch */
      }
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    const nameX = logoX + logoSize + 5;
    const companyNameText = doc.splitTextToSize(
      (company?.name || "COMPANY NAME").toUpperCase(),
      pageWidth - nameX - 15,
    );
    doc.text(companyNameText, nameX, 18);

    let currentY = 18 + companyNameText.length * 6;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);

    const addressText = doc.splitTextToSize(
      company?.address || "Company Address",
      pageWidth - nameX - 15,
    );
    doc.text(addressText, nameX, currentY);
    currentY += addressText.length * 5;

    doc.text(`Email: ${company?.email || "-"}`, nameX, currentY);
    currentY += 5;
    doc.text(`Phone: ${company?.contact || "-"}`, nameX, currentY);
    currentY += 5;

    doc.line(10, currentY + 4, pageWidth - 10, currentY + 4);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text("Group Loan Closer Report", pageWidth / 2, currentY + 14, {
      align: "center",
    });

    const tableColumn = [
      "SN",
      "Account No",
      "Customer Name",
      "Loan Type",
      "Loan Amount",
      "Group Name",
      "Group Leader Name",
      "ROI (%)",
      "Applied Date",
      "Status",
    ];
    const tableRows = filteredLoans.map((item, index) => [
      index + 1,
      item.loanAccountNumber || "N/A",
      item.memberName || "N/A",
      `Group Loan ${item?.loanType ? `(${item?.loanType})`:""}`,
      item.loanAmount || "N/A",
      item.groupName || "N/A",
      item.groupLeaderName || "N/A",
      item.loanRoi || "N/A",
      item.loanAppliedDate ? formatDateDDMMYY(item.loanAppliedDate) : "N/A",
      item.status || "N/A",
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: currentY + 20,
      theme: "grid",
      styles: { fontSize: 7, cellPadding: 3, halign: "center" },
    });

    doc.save("Group_Loan_Report.pdf");
  };

  // Helper dynamic badge colors logic
  const getStatusStyle = (status) => {
    switch (status?.toUpperCase()) {
      case "ACTIVE":
        return {
          backgroundColor: "#e6f4ea",
          color: "#137333",
          padding: "4px 8px",
          borderRadius: "4px",
          fontWeight: "bold",
        };
      case "CLOSED":
        return {
          backgroundColor: "#fce8e6",
          color: "#c5221f",
          padding: "4px 8px",
          borderRadius: "4px",
          fontWeight: "bold",
        };
      case "PENDING":
        return {
          backgroundColor: "#ffe0b2",
          color: "#e65100",
          padding: "4px 8px",
          borderRadius: "4px",
          fontWeight: "bold",
        };
      default:
        return {
          backgroundColor: "#f1f3f4",
          color: "#3c4043",
          padding: "4px 8px",
          borderRadius: "4px",
          fontWeight: "bold",
        };
    }
  };

  if (loading) {
    return (
      <div className="gl-loading-wrapper">
        <div className="spinner"></div>
        <p>Loading loan report...</p>
      </div>
    );
  }

  return (
    <div className="gledr-container">
      {/* Back wrapper */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 16px",
          backgroundColor: "#fff",
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          marginBottom: "20px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}
      >
        <ArrowLeftIcon
          onClick={() => navigate(-1)}
          style={{
            cursor: "pointer",
            padding: "4px",
            borderRadius: "4px",
            transition: "background-color 0.2s",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "#f5f5f5")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "transparent")
          }
        />

        <h2
          style={{
            margin: 0,
            fontSize: "18px",
            fontWeight: "600",
            color: "#333",
          }}
        >
          Group Loan Closer
        </h2>

        <ArrowRightIcon
          onClick={() => navigate(1)}
          style={{
            cursor: "pointer",
            padding: "4px",
            borderRadius: "4px",
            transition: "background-color 0.2s",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "#f5f5f5")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "transparent")
          }
        />
      </div>

      {/* FILTER BOX 1 */}
      <div className="gledr-filterBox">
        <div className="gledr-filter-title">Filter By</div>
        <div className="gledr-filter-wrapper">
          <div className="gledr-filter-item">
            <div className="gledr-filter">
              <label>Loan Type</label>
              <select
                value={loanTypeFilter}
                onChange={(e) => setLoanTypeFilter(e.target.value)}
              >
                <option value="">Select Type</option>
                <option value="FIXED">Fixed Loan</option>
                <option value="INDIVIDUAL">Individual Loan</option>
              </select>
            </div>

            <div className="gledr-filter">
              <label>Select Center</label>
              <select
                value={selectedCenter}
                onChange={(e) => {
                  setSelectedCenter(e.target.value);
                  setSelectedGroup("");
                }}
              >
                <option value="">All Centers</option>
                {centers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.CenterName}
                  </option>
                ))}
              </select>
            </div>

            <div className="gledr-filter">
              <label>Select Group</label>
              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                disabled={!selectedCenter}
              >
                <option value="">All Groups</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.groupname}, {g.groupcode}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="gledr-filter-submit-btn">
            <button className="submit-btn" onClick={handleFilter}>
              submit
            </button>
          </div>
        </div>
      </div>

      {/* FILTER BOX 2 */}
      <div className="gledr-filterBox">
        <div className="gledr-filter-wrapper">
          <div className="gledr-filter-item">
            <div className="gledr-filter">
              <input
                type="date"
                value={filters.loanAppliedDate}
                onChange={(e) =>
                  handleFilterChange("loanAppliedDate", e.target.value)
                }
              />
            </div>
            <div className="gledr-filter">
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <option value="">All Status</option>
                <option value="ACTIVE">ACTIVE</option>
                <option value="pending">pending</option>
                <option value="rejected">Rejected</option>
                <option value="CLOSED">CLOSED</option>
                <option value="FORCE_CLOSED">Force Closed</option>
              </select>
            </div>
            <div className="gledr-filter">
              <input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <div
              className="gledr-report-filter-inputs"
              style={{ color: "white" }}
            >
              <label>Show </label>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  const value = e.target.value;
                  setItemsPerPage(value === "All" ? "All" : Number(value));
                  setCurrentPage(1);
                }}
              >
                {ITEMS_PER_PAGE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <span> entries</span>
            </div>
          </div>
        </div>
      </div>

      {/* DOWNLOAD ACTIONS ROW */}
      {filteredLoans.length > 0 && (
        <div
          className="gledr-pdf-download-btns"
          style={{ marginBottom: "0px" }}
        >
          <button onClick={exportCSV}>
            <Download size={15} /> CSV
          </button>
          <button onClick={exportExcel}>
            <Download size={15} /> Excel
          </button>
          <button onClick={exportPDF}>
            <Download size={15} /> PDF
          </button>
        </div>
      )}

      {/* CORE DISPLAY TABLE */}
      <div className="gledr-report-table-container">
        <table className="gledr-report-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Account No</th>
              <th>Loan Type</th>
              <th>Loan Amount</th>
              <th>Customer name</th>
              <th>Group Leader Name</th>
              <th>Group</th>
              <th>ROI (%)</th>
              <th>Tenure</th>
              <th>Frequency</th>
              <th>Applied Date</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredLoans.length ? (
              filteredLoans.map((item, i) => (
                <tr key={item.id}>
                  <td>
                    {itemsPerPage === "All"
                      ? i + 1
                      : (currentPage - 1) * itemsPerPage + i + 1}
                  </td>
                  <td>{item.loanAccountNumber || "N/A"}</td>
                  <td>{`Group Loan ${item?.loanType ? `(${item?.loanType})`:""}`}</td>
                  <td>{item.loanAmount || "N/A"}</td>
                  <td>{item.memberName || "N/A"}</td>
                  <td>{item.groupLeaderName || "N/A"}</td>
                  <td>{item.groupName || "N/A"}</td>
                  <td>{item.loanRoi || "N/A"}</td>
                  <td>{item.loanTenure || "N/A"}</td>
                  <td>{item.loanRecoveryFrequency || "N/A"}</td>
                  <td>
                    {item.loanAppliedDate
                      ? new Date(item.loanAppliedDate).toLocaleDateString(
                          "en-GB",
                        )
                      : "-"}
                  </td>
                  <td>
                    <span style={getStatusStyle(item.status)}>
                      {item.status}
                    </span>
                  </td>
                  <td>
                    {item.status?.toLowerCase() === "active" ? (
                      <button
                        className="BranchLoanReport-btn-loan-card close-action-btn"
                        onClick={() => handleLoanClose(item)}
                      >
                        Close
                      </button>
                    ) : item.status?.toLowerCase() === "closed" ? (
                      <button
                        className="BranchLoanReport-btn-loan-card noc-action-btn"
                        style={{
                          backgroundColor: "#003366",
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                        onClick={() => handleViewNocLetter(item)}
                      >
                        <FileText size={14} /> NOC Letter
                      </button>
                    ) :item.status==="FORCE_CLOSED" ?(
                      <button
                        className="BranchLoanReport-btn-loan-card noc-action-btn"
                        style={{
                          backgroundColor: "#003366",
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                        onClick={() => handleViewNocLetter(item)}
                      >
                        <FileText size={14} /> NOC Letter
                      </button>
                    ): (
                      <span className="BranchLoanReport-na">N/A</span>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="13" align="center">
                  No data found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      {itemsPerPage !== "All" && (
        <div className="gledr-pagination">
          <button
            disabled={currentPage === 1 || filteredLoans.length === 0}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            ⬅ Prev
          </button>
          <span>
            {filteredLoans.length === 0
              ? "No data"
              : `Page ${currentPage} of ${totalPages}`}
          </span>
          <button
            disabled={currentPage === totalPages || filteredLoans.length === 0}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            Next ➡
          </button>
        </div>
      )}

      {/* ================== LOAN CLOSING CONFIRMATION MODAL ================== */}
      {isModalOpen && (
        <div className="loan-closer-modal-overlay">
          <div className="loan-closer-modal-box">
            <div className="loan-closer-modal-header">
              <h2>Confirm Loan Account Closure</h2>
            </div>
            <div className="loan-closer-modal-body">
              <p>
                Are you sure you want to permanently close this loan account?
              </p>
              {selectedLoanToClose && (
                <div className="loan-modal-details-card">
                  <p>
                    <strong>Customer:</strong> {selectedLoanToClose.memberName}
                  </p>
                  <p>
                    <strong>Account No:</strong>{" "}
                    {selectedLoanToClose.loanAccountNumber}
                  </p>
                  <p>
                    <strong>Loan Amount:</strong> ₹
                    {selectedLoanToClose.loanAmount}
                  </p>
                </div>
              )}
              <p className="modal-warning-text">
                ⚠️ Warning: This action cannot be undone.
              </p>
            </div>
            <div className="loan-closer-modal-footer">
              <button
                className="modal-btn cancel-btn"
                disabled={closingSubmitLoading}
                onClick={() => {
                  setIsModalOpen(false);
                  setSelectedLoanToClose(null);
                }}
              >
                Cancel
              </button>
              <button
                className="modal-btn confirm-btn"
                disabled={closingSubmitLoading}
                onClick={handleConfirmCloseLoan}
              >
                {closingSubmitLoading ? "Processing..." : "Yes, Close Account"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupLoanCloser;
